/**
 * component-custom-product-card.js
 *
 * Gère le comportement de la carte produit custom :
 *  - Mise à jour du prix et du CTA au changement de variante
 *  - Mise à jour du badge de concentration (metafields par variante, extension future)
 *  - Ajout au panier via fetch + dispatch PUB_SUB_EVENTS.cartUpdate
 *
 * Dépendances globales (chargées dans theme.liquid) :
 *  - pubsub.js  → publish()
 *  - constants.js → PUB_SUB_EVENTS
 *  - window.routes.cart_add_url
 */

if (!customElements.get('cct-add-to-cart')) {
  class CctAddToCart extends HTMLElement {
    connectedCallback() {
      this._btn    = this.querySelector('.cct-card__atc-btn');
      this._prices = this.querySelector('.cct-card__prices');
      this._card   = this.closest('.cct-card');
      this._select = this._card && this._card.querySelector('.cct-card__variant-select');

      if (this._btn && this._btn.tagName === 'BUTTON') {
        this._btn.addEventListener('click', () => this._addToCart());
      }
      if (this._select) {
        this._select.addEventListener('change', (e) => this._onVariantChange(e.target));
      }
    }

    _onVariantChange(select) {
      const opt          = select.options[select.selectedIndex];
      const price        = parseInt(opt.dataset.price, 10);
      const comparePrice = parseInt(opt.dataset.comparePrice, 10);
      const available    = opt.dataset.available === 'true';

      /* Mise à jour des prix affichés — on privilégie la chaîne déjà
         formatée par Shopify (filtre money, data-price-money) pour garder
         le bon symbole monétaire (£) ; repli sur _money() sinon. */
      if (this._prices) {
        const priceNow = this._prices.querySelector('.cct-card__price-now');
        const priceWas = this._prices.querySelector('.cct-card__price-was');
        if (priceNow) priceNow.textContent = opt.dataset.priceMoney || this._money(price);
        if (priceWas) {
          if (comparePrice > price) {
            priceWas.textContent = opt.dataset.compareMoney || this._money(comparePrice);
            priceWas.hidden = false;
          } else {
            priceWas.hidden = true;
          }
        }
      }

      /* Mise à jour de l'ID variante sur le bouton */
      if (this._btn && this._btn.tagName === 'BUTTON') {
        this._btn.dataset.variantId = opt.value;
      }

      /* Disponibilité → CTA */
      this._updateCta(available);

      /* Badge concentration (extension variante, voir CLAUDE.md) */
      this._updatePpgLine(opt);
    }

    _updateCta(available) {
      if (!this._btn || this._btn.tagName !== 'BUTTON') return;
      const label = this._btn.querySelector('.cct-card__atc-label');
      this._btn.disabled = !available;
      if (label) label.textContent = available ? 'ADD TO CART' : 'SOLD OUT';
    }

    _updatePpgLine(opt) {
      const ppgEl = this._card && this._card.querySelector('.cct-card__ppg');
      if (!ppgEl) return;

      /*
       * data-min-ppg    : "From £3.22/g" — pré-formaté en Liquid, toujours le min absolu
       * data-base-*     : concentration au niveau produit
       * data-concentration / data-molecule : concentration au niveau variante (extension future)
       */
      const varConc  = opt.dataset.concentration;
      const varMol   = opt.dataset.molecule;
      const showConc = ppgEl.dataset.showConc === 'true';
      const baseConc = ppgEl.dataset.baseConc;
      const baseMol  = ppgEl.dataset.baseMolecule;
      const minPpg   = ppgEl.dataset.minPpg || '';

      const conc = (varConc && varMol) ? varConc : (showConc ? baseConc : null);
      const mol  = (varConc && varMol) ? varMol  : (showConc ? baseMol  : null);

      /* Mise à jour ciblée du badge — ne touche pas au texte "From £X/g" */
      const badge = ppgEl.querySelector('.cct-card__conc-badge');
      if (badge) {
        if (conc && mol) {
          badge.textContent = `${conc}% ${mol}`;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }

      /* Maintient la hauteur de ligne si aucun contenu — on préserve le badge culture */
      const cultureBadge = ppgEl.querySelector('.cct-card__culture-badge');
      if (!badge && !minPpg && !cultureBadge) ppgEl.textContent = ' ';
    }

    async _addToCart() {
      const variantId = this._btn && this._btn.dataset.variantId;
      if (!variantId) return;

      this._btn.disabled = true;
      this._btn.classList.add('loading');
      const label        = this._btn.querySelector('.cct-card__atc-label');
      const originalText = label ? label.textContent : 'ADD TO CART';

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        /* On demande la section cart-icon-bubble pour mettre à jour le compteur
           sans ouvrir le tiroir panier. */
        formData.append('sections', 'cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        });

        const response = await res.json();

        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cct-add-to-cart',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        }

        /* Mise à jour du compteur de l'icône panier, sans ouvrir le tiroir. */
        if (response.sections && response.sections['cart-icon-bubble']) {
          const iconBubble = document.getElementById('cart-icon-bubble');
          if (iconBubble) {
            const newHTML = new DOMParser()
              .parseFromString(response.sections['cart-icon-bubble'], 'text/html')
              .querySelector('.shopify-section');
            if (newHTML) iconBubble.innerHTML = newHTML.innerHTML;
          }
        }

        /* On notifie les autres composants (sans déclencher l'ouverture du tiroir). */
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cct-add-to-cart',
          productVariantId: variantId,
          cartData: response,
        });

      } catch (err) {
        console.error('[CctAddToCart]', err);
      } finally {
        this._btn.disabled = false;
        this._btn.classList.remove('loading');
        if (label) label.textContent = originalText;
      }
    }

    _money(cents) {
      /* Repli uniquement : le chemin principal utilise data-price-money
         (formaté par Shopify). Shopify.currency.active renvoie le CODE ISO
         ("GBP"), pas le symbole — on force donc le symbole £ pour la boutique UK. */
      return `£${(cents / 100).toFixed(2)}`;
    }
  }

  customElements.define('cct-add-to-cart', CctAddToCart);
}
